import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { BrandGroupSelect } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { createFormHelper } from "lib/Form";
import { LEF_LEAD_KINDS } from "models";
import { useTagsQuery } from "modules/CheckInServices/components/LocationServicesList/hooks";
import { enumToDropdownOptions } from "utils/common";

import type { LocationServiceFormValues } from "modules/CheckInServices/components/LocationServiceModal";

const FormHelper = createFormHelper<LocationServiceFormValues>();

export const LocationServiceForm = () => {
  const { t } = useTranslation();

  const { tagsOptions } = useTagsQuery();
  const { selectedLocation } = useSelectedLocation();

  const { watch, setValue } = FormHelper.useFormContext();
  const sendToLef = watch("send_to_lef");

  const lefLeadKindOptions = enumToDropdownOptions({ enumObj: LEF_LEAD_KINDS, t });
  const isSendToLefVisible = selectedLocation?.is_lef_enabled && selectedLocation.lef_account_number;

  const handleSendToLefChange = (checked: boolean) => {
    setValue("send_to_lef", checked);
    if (!checked) setValue("lef_lead_kind", null);
    else setValue("lef_lead_kind", LEF_LEAD_KINDS.Taxation);
  };

  return (
    <>
      <Form.Field>
        <label>{t("v8_service_name", "Name")}</label>
        <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true, disabled: true }} />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_service_description", "Service description")}</label>
        <FormHelper.Field.Input name="description" inputProps={{ placeholder: t("v8_description", "Description"), fluid: true, disabled: true }} />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_brand_groups", "Brand Groups")}</label>
        <FormHelper.Controller name="brand_group_ids" render={({ field }) => <BrandGroupSelect disabled value={field.value} onChange={field.onChange} />} />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_select_tag", "Select tag")}</label>
        <FormHelper.Field.Dropdown
          name="tag_id"
          options={tagsOptions}
          dropdownProps={{ placeholder: t("v8_tags", "Tags"), fluid: true, clearable: true, disabled: true }}
        />
      </Form.Field>

      <Form.Field>
        <label>{t("v8_info_url", "Info URL")}</label>
        <FormHelper.Field.Input name="info_url" inputProps={{ placeholder: t("v8_url", "URL"), fluid: true, disabled: true }} />
      </Form.Field>

      <Form.Field>
        <label>{`${t("v8_price_excl_vat", "Price excl. VAT")}`}</label>
        <FormHelper.Field.Input name="price" inputProps={{ placeholder: t("v8_price", "Price"), fluid: true, disabled: true }} isPriceInput />
      </Form.Field>

      <Form.Group inline>
        <Form.Field>
          <FormHelper.Field.Checkbox name="hide_for_lease_appointments" label={t("v8_hide_for_lease_appointments", "Hide for lease appointments")} disabled />
        </Form.Field>

        <Form.Field>
          <FormHelper.Field.Checkbox name="can_convert_to_intervention" label={t("v8_convert_to_intervention", "Convert to intervention")} disabled />
        </Form.Field>

        {isSendToLefVisible && (
          <Form.Field>
            <FormHelper.Field.Checkbox name="send_to_lef" label={t("v8_send_to_lef", "Send to LEF")} onChange={checked => handleSendToLefChange(checked)} />
          </Form.Field>
        )}
      </Form.Group>

      {isSendToLefVisible && sendToLef && (
        <Form.Field>
          <FormHelper.Field.Dropdown
            name="lef_lead_kind"
            options={lefLeadKindOptions}
            dropdownProps={{ fluid: true, selectOnBlur: false, placeholder: t("v8_lef_lead_kind", "LEF Lead Kind ID") }}
          />
        </Form.Field>
      )}

      <Form.Field>
        <label>{t("v8_notification_email", "Notification email")}</label>
        <FormHelper.Field.Input name="notification_email" inputProps={{ placeholder: t("v8_email", "Email"), fluid: true }} />
      </Form.Field>
    </>
  );
};
